import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { ArrowLeft, Save, X } from 'lucide-react';
import { apiService } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { FormInput } from '@/components/ui/FormInput';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Alert } from '@/components/ui/Alert';
import { ProductVariantManager } from '@/components/admin/ProductVariantManager';
import { UpdateProductRequest, Category, ProductVariant } from '@/types';
import { AdminImageManager } from '@/components/admin/AdminImageManager';

export const ProductEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  console.log('ProductEdit - URL ID from useParams:', id);
  console.log('ProductEdit - Current URL:', window.location.href);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [variants, setVariants] = useState<ProductVariant[]>([]);

  // Fetch categories from database
  const { data: categoriesData, isLoading: categoriesLoading } = useQuery(
    ['categories'],
    () => apiService.getCategories(),
    { keepPreviousData: true, staleTime: 15 * 60 * 1000 }
  );

  const categories = categoriesData?.data || [];

  const { data, isLoading, error } = useQuery(
    ['admin-product', id],
    () => apiService.getAdminProduct(Number(id)),
    {
      enabled: !!id,
      onSuccess: (response) => {
        console.log('ProductEdit - API Response received:', response);
      console.log('ProductEdit - Product data:', response.data);
      console.log('ProductEdit - Category from API:', response.data?.category);
      console.log('ProductEdit - FULL API RESPONSE:', JSON.stringify(response.data, null, 2));
      }
    }
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<UpdateProductRequest>({
    defaultValues: {
      name: '',
      description: '',
      price: 0,
      categoryId: 0,
      isActive: true,
      quantity: 0,
    },
  });

  // Watch form values for debugging
  const watchedValues = watch();
  console.log('ProductEdit - Current form values:', watchedValues);
  console.log('ProductEdit - Category value:', watchedValues.categoryId);
  console.log('ProductEdit - Variants state:', variants);
  console.log('ProductEdit - Data from API:', data?.data);
  console.log('ProductEdit - Product variants from data:', data?.data?.variants);

  // Reset form when product data loads
  React.useEffect(() => {
    if (data?.data) {
      const product = data.data;
      console.log('ProductEdit - Loading product data:', product);
      console.log('ProductEdit - Product category value:', product.category);
      console.log('ProductEdit - Product category type:', typeof product.category);
      console.log('ProductEdit - Product has variants property:', 'variants' in product);
      console.log('ProductEdit - Product variants value:', product.variants);
      console.log('ProductEdit - Product variants type:', typeof product.variants);
      console.log('ProductEdit - Product variants length:', product.variants?.length);
      console.log('ProductEdit - All product keys:', Object.keys(product));
      
      // Reset form with all values - handle category object
      reset({
        name: product.name,
        description: product.description,
        price: product.price,
        categoryId: (typeof product.category === 'object' && product.category !== null) ? (product.category as Category).id : 0,
        isActive: product.isActive,
        quantity: product.quantity,
      });
      
      // Set variants from product data
      console.log('ProductEdit - Product variants from API:', product.variants);
      setVariants(product.variants || []);
      
      console.log('ProductEdit - Form reset with category:', product.category);
    }
  }, [data, reset]);

  // Additional effect to ensure category is set after form reset
  React.useEffect(() => {
    if (data?.data) {
      // Use setTimeout to ensure this runs after the reset
      setTimeout(() => {
        const categoryId = data.data ? ((typeof data.data.category === 'object' && data.data.category !== null) ? (data.data.category as Category).id : 0) : 0;
        setValue('categoryId', categoryId);
        console.log('ProductEdit - Category setValue called with:', categoryId);
      }, 100);
    }
  }, [data, setValue]);

  const updateProductMutation = useMutation(
    (productData: UpdateProductRequest) => apiService.updateProduct(Number(id), productData),
    {
      onSuccess: (response) => {
        console.log('ProductEdit - Update successful, response:', response);
        console.log('ProductEdit - Invalidating queries...');
        
        // Invalidate all related queries more aggressively
        queryClient.invalidateQueries(['admin-product', id]);
        queryClient.invalidateQueries(['product', id]);
        queryClient.invalidateQueries(['admin-products']);
        queryClient.invalidateQueries(['products']);
        queryClient.invalidateQueries(['products', 'category']);
        
        // Force refetch of all products queries (with any parameters)
        queryClient.refetchQueries({ predicate: (query) => 
          query.queryKey[0] === 'admin-products' || 
          query.queryKey[0] === 'products' ||
          query.queryKey[0] === 'admin-product'
        });
        
        // Wait a moment for cache to update before navigating
        setTimeout(() => {
          console.log('ProductEdit - Navigating to products list');
          navigate('/admin/products');
        }, 100);
      },
      onError: (error: unknown) => {
        console.log('ProductEdit - Update failed, error:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to update product';
        setSubmitError(errorMessage);
      },
    }
  );


  const onSubmit = async (formData: UpdateProductRequest) => {
    setIsSubmitting(true);
    setSubmitError(null);
    
    // Validate required fields
    if (formData.name && !formData.name.trim()) {
      setSubmitError('Product name is required');
      setIsSubmitting(false);
      return;
    }
    if (formData.description && !formData.description.trim()) {
      setSubmitError('Product description is required');
      setIsSubmitting(false);
      return;
    }
    if (formData.description && formData.description.trim().length < 10) {
      setSubmitError('Product description must be at least 10 characters long');
      setIsSubmitting(false);
      return;
    }
    if (formData.categoryId && (!formData.categoryId || formData.categoryId === 0)) {
      setSubmitError('Please select a category');
      setIsSubmitting(false);
      return;
    }
    if (formData.price && formData.price <= 0) {
      setSubmitError('Price must be greater than 0');
      setIsSubmitting(false);
      return;
    }
    if (formData.quantity && formData.quantity < 0) {
      setSubmitError('Quantity cannot be negative');
      setIsSubmitting(false);
      return;
    }
    
      console.log('=== DEBUG: PRODUCT EDIT SUBMISSION ===');
      console.log('ProductEdit - Form data being submitted:', formData);
      console.log('ProductEdit - Variants being submitted:', variants);
      console.log('ProductEdit - Raw variants data:', JSON.stringify(variants, null, 2));
    
    try {
      // Only send variants if they've been modified from original
      const originalVariants = data?.data?.variants || [];
      const variantsModified = variants.length !== originalVariants.length || 
        variants.some(variant => !originalVariants.some((orig: ProductVariant) => 
          orig.color === variant.color && 
          orig.size === variant.size && 
          orig.quantity === variant.quantity
        ));

      console.log('=== DEBUG: VARIANT COMPARISON ===');
      console.log('ProductEdit - Original variants count:', originalVariants.length);
      console.log('ProductEdit - Original variants raw:', JSON.stringify(originalVariants, null, 2));
      console.log('ProductEdit - Current variants count:', variants.length);
      console.log('ProductEdit - Current variants raw:', JSON.stringify(variants, null, 2));
      console.log('ProductEdit - Variants modified:', variantsModified);

      const productData = { ...formData } as any;
      
      if (variantsModified) {
        // Send variants only if they've been modified
        const cleanedVariants = variants.map(variant => ({
          color: variant.color,
          size: variant.size,
          quantity: variant.quantity
        }));
        
        console.log('=== DEBUG: SENDING VARIANTS ===');
        console.log('ProductEdit - Sending variants (modified):', cleanedVariants);
        console.log('ProductEdit - Cleaned variants raw:', JSON.stringify(cleanedVariants, null, 2));
        productData.variants = cleanedVariants;
      } else {
        console.log('=== DEBUG: NOT SENDING VARIANTS ===');
        console.log('ProductEdit - Not sending variants (unchanged)');
        // Don't include variants field at all
      }
      
      console.log('=== DEBUG: FINAL PAYLOAD ===');
      console.log('ProductEdit - Final payload:', JSON.stringify(productData, null, 2));
      console.log('ProductEdit - Payload size:', JSON.stringify(productData).length, 'bytes');
      
      await updateProductMutation.mutateAsync(productData);
    } catch (error) {
      // Error handled in mutation
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto">
        <Alert type="error" title="Product not found">
          The product you're trying to edit doesn't exist or has been removed.
          <br />
          <br />
          Available products: 1, 2, 3, 4
        </Alert>
        <div className="mt-6">
          <Button onClick={() => navigate('/admin/products')}>
            Back to Products
          </Button>
        </div>
      </div>
    );
  }

  if (!data?.data) {
    return (
      <Alert type="error" title="Product not found">
        The requested product could not be found.
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Button
          variant="outline"
          onClick={() => navigate('/admin/products')}
          className="flex items-center"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Products
        </Button>
        <div>
          <h1 className="text-3xl font-bold text-secondary-900 dark:text-white">Edit Product</h1>
          <p className="text-secondary-600">Update product information</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Product Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {submitError && (
              <Alert type="error" title="Update Failed">
                {submitError}
              </Alert>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Product Name *
                  </label>
                  <FormInput
                    {...register('name', { required: 'Product name is required' })}
                    placeholder="Enter product name"
                    error={errors.name?.message}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Price *
                  </label>
                  <FormInput
                    {...register('price', { 
                      required: 'Price is required',
                      min: { value: 0.01, message: 'Price must be greater than 0' }
                    })}
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    error={errors.price?.message}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Stock Quantity *
                  </label>
                  <FormInput
                    {...register('quantity', { 
                      required: 'Stock quantity is required',
                      min: { value: 0, message: 'Quantity must be 0 or greater' }
                    })}
                    type="number"
                    min="0"
                    placeholder="0"
                    error={errors.quantity?.message}
                  />
                  <p className="mt-1 text-xs text-secondary-500">
                    Set to 0 to mark as sold out
                  </p>
                </div>

                <div className="col-span-2">
                  <ProductVariantManager
                    variants={variants}
                    onChange={setVariants}
                    productName={watchedValues.name}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Category *
                    {watchedValues.categoryId && watchedValues.categoryId > 0 && (
                      <span className="ml-2 text-sm text-green-600 font-normal">
                        (Current: {categories.find(cat => cat.id === watchedValues.categoryId)?.name || 'Unknown'})
                      </span>
                    )}
                  </label>
                  {categoriesLoading ? (
                    <div className="w-full px-3 py-2 border border-secondary-300 rounded-md bg-gray-50 animate-pulse">
                      Loading categories...
                    </div>
                  ) : (
                    <select
                      value={watchedValues.categoryId || ''}
                      onChange={(e) => setValue('categoryId', parseInt(e.target.value) || 0)}
                      className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required
                    >
                      <option value="">Select a category</option>
                      {categories.map((category: Category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {!watchedValues.categoryId && (
                    <p className="mt-1 text-sm text-error-600">Category is required</p>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    {...register('description', { required: 'Description is required' })}
                    rows={4}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    placeholder="Enter product description"
                  />
                  {errors.description && (
                    <p className="mt-1 text-sm text-error-600">{errors.description.message}</p>
                  )}
                </div>

                <div>
                  <label className="flex items-center space-x-2">
                    <input
                      {...register('isActive')}
                      type="checkbox"
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
                    />
                    <span className="text-sm font-medium text-secondary-700">
                      Product is active
                    </span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-4 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/admin/products')}
                disabled={isSubmitting}
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center"
              >
                {isSubmitting ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {isSubmitting ? 'Updating...' : 'Update Product'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Multiple Images Management */}
      {data?.data && (
        <Card>
          <CardContent className="pt-6">
            <AdminImageManager 
              productId={data.data.id} 
              onImagesChange={(images) => {
                // Optional: Handle images change if needed
                console.log('Images updated:', images);
              }}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};
